import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading } from "@/components/States";
import { inputCls, btnPrimary, label } from "@/lib/ui";

const Box = ({ title, children }) => (
  <div className="max-w-md mx-auto py-6 sm:py-12">
    <span className="h-11 w-11 rounded-2xl bg-blue-600 text-white flex items-center justify-center font-black text-xl mb-5">F</span>
    <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 mb-6">{title}</h1>
    {children}
  </div>
);

const Note = ({ testId, tone = "blue", children }) => (
  <p data-testid={testId} className={`text-sm rounded-xl p-4 border ${tone === "red" ? "bg-red-50 border-red-200 text-red-700" : "bg-blue-50 border-blue-200 text-slate-700"}`}>{children}</p>
);

// Read the one-time token, then strip it from the address bar so it isn't kept in history.
const useUrlToken = () => {
  const ref = useRef(new URLSearchParams(window.location.search).get("token"));
  useEffect(() => {
    if (ref.current) window.history.replaceState(null, "", window.location.pathname);
  }, []);
  return ref.current;
};

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setMsg(data.message);
    } catch (e2) {
      setErr(errMsg(e2));
    }
  };
  return (
    <Box title="Reset your password">
      {msg ? <Note testId="forgot-sent">{msg}</Note> : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <div><span className={label}>Email</span><input data-testid="forgot-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} /></div>
          {err && <Note testId="forgot-error" tone="red">{err}</Note>}
          <button data-testid="forgot-submit-btn" className={btnPrimary + " w-full"}>Email me a reset link</button>
        </form>
      )}
      <p className="text-sm text-center mt-6"><Link to="/login" className="font-semibold text-blue-600">Back to sign in</Link></p>
    </Box>
  );
}

export function ResetPassword() {
  const token = useUrlToken();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState(token ? "" : "This reset link is missing its token.");
  const [done, setDone] = useState("");
  const [mismatch, setMismatch] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setMismatch(pw !== pw2);
    if (pw !== pw2) return setErr("Passwords don't match.");
    setErr("");
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: pw });
      localStorage.removeItem("ft_token");
      if (data.device_token) localStorage.setItem("ft_device", data.device_token);
      setDone(data.message);
      toast.success("Password updated");
    } catch (e2) {
      setErr(errMsg(e2));
    }
  };
  return (
    <Box title="Choose a new password">
      {done ? (
        <>
          <Note testId="reset-success">{done}</Note>
          <button data-testid="reset-signin-btn" onClick={() => { window.location.href = "/login"; }} className={btnPrimary + " w-full mt-4"}>Sign in</button>
        </>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
          <div><span className={label}>New password (10+ characters)</span><input data-testid="reset-password-input" type="password" minLength={10} required value={pw} onChange={(e) => setPw(e.target.value)} className={inputCls} /></div>
          <div><span className={label}>Repeat new password</span><input data-testid="reset-password2-input" type="password" minLength={10} required value={pw2} onChange={(e) => setPw2(e.target.value)} className={inputCls} /></div>
          {err && <Note testId="reset-error" tone="red">{err} {!mismatch && <Link to="/forgot-password" className="underline">Request a new link</Link>}</Note>}
          <button data-testid="reset-submit-btn" disabled={!token} className={btnPrimary + " w-full"}>Update password</button>
        </form>
      )}
      {!done && <p className="text-sm text-center mt-6"><button onClick={() => nav("/login")} className="font-semibold text-blue-600">Back to sign in</button></p>}
    </Box>
  );
}

export function VerifyEmail() {
  const token = useUrlToken();
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [err, setErr] = useState("");
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) {
      setErr("This confirmation link is missing its token.");
      return;
    }
    api.post("/auth/verify-email", { token })
      .then(({ data }) => { signIn(data); toast.success(`Welcome, ${data.user.first_name}! Your email is confirmed.`); nav("/", { replace: true }); })
      .catch((e) => setErr(errMsg(e)));
  }, [token, signIn, nav]);
  return (
    <Box title="Confirming your email">
      {err ? <Note testId="verify-error" tone="red">{err} <Link to="/login" className="underline">Sign in</Link></Note> : <Loading label="Confirming…" />}
    </Box>
  );
}
